<template>
  <!-- ===================================================================
       ARTICLE TEMPLATE — the standard starting point for every article.
       To create a new article:
         1. Copy this file to src/views/articles/<Slug>.vue
         2. Replace the hero, body, and related sections with real content,
            using ONLY the components shown here (a-hero, a-body h2/h3, lists,
            a-callout, blockquote, figure + a-ph, a-cta-*).
         3. Swap each grey <div class="a-ph"> for a real <img> when art exists.
         4. Add a registry entry in src/articles/registry.js (slug + SEO meta).
       The shared header + footer come from the App shell — do not add them here.
       =================================================================== -->
  <div class="article-page">
    <article>

      <!-- Hero -->
      <div class="a-hero a-wrap">
        <p class="a-eyebrow">Category / Topic</p>
        <h1>The Article Headline Goes Here and Can Run Two Lines</h1>
        <p class="a-deck">The deck (or standfirst) sits under the headline: one sentence that sets up the promise of the piece and earns the scroll.</p>
        <div class="a-byline">
          <span class="a-author">Author Name</span>
          <span class="a-sep">·</span>
          <span>1 January 2026</span>
          <span class="a-sep">·</span>
          <span>6 min read</span>
        </div>
      </div>

      <!-- Hero image (grey placeholder → replace with <img>) -->
      <div class="a-wrap-wide">
        <figure class="a-full">
          <div class="a-ph wide">Hero image · 21:9</div>
          <figcaption>Caption for the lead image — credit the artist or the artwork here.</figcaption>
        </figure>
      </div>

      <!-- Body -->
      <div class="a-body a-wrap">
        <p class="a-lead">The opening paragraph is a lead: slightly larger, it restates the hook in plain language and tells the reader exactly what they'll get. Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore.</p>

        <p>Regular body copy. Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat.</p>

        <p>A second paragraph with an <a href="/">inline link</a> and some <strong>emphasised text</strong> to show how those read. Excepteur sint occaecat cupidatat non proident, sunt in culpa qui officia deserunt mollit anim id est laborum.</p>

        <h2>A Section Heading (H2)</h2>
        <p>Sed ut perspiciatis unde omnis iste natus error sit voluptatem accusantium doloremque laudantium, totam rem aperiam, eaque ipsa quae ab illo inventore veritatis et quasi architecto beatae vitae dicta sunt explicabo.</p>

        <figure>
          <div class="a-ph">Inline image · 16:9</div>
          <figcaption>A caption describing what this image shows.</figcaption>
        </figure>

        <p>Nemo enim ipsam voluptatem quia voluptas sit aspernatur aut odit aut fugit, sed quia consequuntur magni dolores eos qui ratione voluptatem sequi nesciunt.</p>

        <h3>A Subheading (H3)</h3>
        <p>Neque porro quisquam est, qui dolorem ipsum quia dolor sit amet, consectetur, adipisci velit, sed quia non numquam eius modi tempora incidunt ut labore et dolore magnam aliquam quaerat voluptatem.</p>

        <ul>
          <li>Bulleted list item one, kept to a single line where possible.</li>
          <li>Item two demonstrates how lists sit in the reading column.</li>
          <li>Item three — ut enim ad minima veniam, quis nostrum exercitationem.</li>
        </ul>

        <div class="a-callout">
          <p class="a-label">Tip</p>
          <p>Use a callout for a practical tip, a definition, or an aside that shouldn't interrupt the main flow. Quis autem vel eum iure reprehenderit qui in ea voluptate.</p>
        </div>

        <h2>Another Section With a Pull Quote</h2>
        <p>At vero eos et accusamus et iusto odio dignissimos ducimus qui blanditiis praesentium voluptatum deleniti atque corrupti quos dolores et quas molestias excepturi sint occaecati.</p>

        <blockquote>
          A pull quote pulls a strong line out of the copy to give the eye a rest and the page some rhythm.
          <cite>— Attribution, Role</cite>
        </blockquote>

        <p>Et harum quidem rerum facilis est et expedita distinctio. Nam libero tempore, cum soluta nobis est eligendi optio cumque nihil impedit quo minus id quod maxime placeat.</p>

        <figure>
          <div style="display:grid;grid-template-columns:1fr 1fr;gap:16px">
            <div class="a-ph square">Before</div>
            <div class="a-ph square">After</div>
          </div>
          <figcaption>A side-by-side comparison — before and after.</figcaption>
        </figure>

        <ol>
          <li>Numbered step one, for how-to sequences.</li>
          <li>Numbered step two.</li>
          <li>Numbered step three.</li>
        </ol>

        <!-- Inline CTA — mid-article conversion for the PPC landing role -->
        <div class="a-cta-inline">
          <h3>Ready to make your own?</h3>
          <p>Create augmented reality art in your browser — no app, no code.</p>
          <a class="a-btn" href="/login">Start creating free</a>
        </div>

        <h2>Closing Section</h2>
        <p>Temporibus autem quibusdam et aut officiis debitis aut rerum necessitatibus saepe eveniet ut et voluptates repudiandae sint et molestiae non recusandae. Itaque earum rerum hic tenetur a sapiente delectus.</p>
      </div>

    </article>

    <!-- End CTA (dark) -->
    <section class="a-cta-end">
      <div class="a-inner">
        <h2>Bring your art to life in AR</h2>
        <p>Publish augmented reality to a link that opens in any phone browser.</p>
        <a class="a-btn" href="/login">Start creating free</a>
      </div>
    </section>

    <!-- Related — renders the shared <ArticleCard>, same component the /articles
         listing and the home teaser use. Drop this section if there's nothing to link. -->
    <section v-if="related.length" class="a-related a-wrap-wide">
      <h2>Related articles</h2>
      <div class="ej-card-grid">
        <ArticleCard v-for="a in related" :key="a.slug" :article="a" />
      </div>
    </section>

  </div>
</template>

<script>
import ArticleCard from '../components/ArticleCard.vue'
import { publishedArticles } from '../articles/registry'

export default {
  name: 'ArticleTemplate',
  components: { ArticleCard },
  computed: {
    related () {
      return publishedArticles.slice(0, 3)
    }
  }
}
</script>
